import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Container,
  AppBar,
  Toolbar,
  CircularProgress,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GavelIcon from "@mui/icons-material/Gavel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  Legend,
} from "recharts";
import { RemoveRedEyeRounded } from "@mui/icons-material";
import { personalDashOtherData } from "../../../static/staticdata";
import { backend_api } from "../../../static/server";
import { getAllBilles } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import {
  getAllStatement,
  getAllAccounts,
} from "../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import { getActivities } from "../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import { getCases } from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { getAllFilterContacts } from "../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { getAllUsers } from "../../../redux/Apps/users/userSlice";

const NoDataFound = ({ title, description }) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={4}
      px={2}
      height="100%"
    >
      <Box
        component="img"
        src="/no-data-illustration.svg"
        alt="No data available"
        sx={{
          width: "8rem",
          height: "8rem",
          mb: 2,
          opacity: 0.6,
        }}
        onError={(e) => {
          e.target.style.display = "none";
          e.target.nextSibling.style.display = "flex";
        }}
      />
      <SearchOffIcon
        sx={{
          display: "none",
          fontSize: "4rem",
          color: "text.secondary",
          mb: 2,
          opacity: 0.6,
        }}
      />
      <Typography
        variant="h6"
        color="text.secondary"
        fontWeight="medium"
        sx={{ fontSize: "1rem", mb: 1 }}
      >
        {title || "No Data Found"}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ fontSize: "0.875rem", textAlign: "center", opacity: 0.8 }}
      >
        {description || "There's no data available to display at the moment."}
      </Typography>
    </Box>
  );
};

const OverviewCard = ({ title, value, change, IconComponent, subtitle }) => {
  const isPositive = change > 0;
  const isNeutral = change === 0;
  const TrendIcon = isNeutral
    ? TrendingFlatIcon
    : isPositive
    ? TrendingUpIcon
    : TrendingDownIcon;

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "0.5rem",
        border: "1px solid",
        borderColor: "divider",
        transition: "all 0.2s ease",
        position: "relative",
      }}
    >
      <CardContent
        sx={{
          p: "1rem",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: "0.2rem",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: "2rem",
              height: "2rem",
            }}
          >
            <IconComponent sx={{ fontSize: "1.125rem" }} />
          </Avatar>
          <Box
            display="flex"
            alignItems="center"
            gap="0.25rem"
            sx={{
              bgcolor: isNeutral
                ? "grey.100"
                : isPositive
                ? "success.50"
                : "error.50",
              px: "0.5rem",
              py: "0.25rem",
              borderRadius: "1rem",
            }}
          >
            <TrendIcon
              sx={{
                color: isNeutral
                  ? "text.secondary"
                  : isPositive
                  ? "success.main"
                  : "error.main",
                fontSize: "0.875rem",
              }}
            />
            <Typography
              sx={{
                color: isNeutral
                  ? "text.secondary"
                  : isPositive
                  ? "success.main"
                  : "error.main",
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              {Math.abs(change)}%
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight="600"
            sx={{
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: "0.25rem",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            fontWeight="700"
            color="text.primary"
            sx={{
              fontSize: "1.2rem",
              lineHeight: 1.2,
              mb: "auto",
            }}
          >
            {value}
          </Typography>
        </Box>

        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.6875rem",
              opacity: 0.8,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Revenue Trend Chart Component
const RevenueTrendChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: "100%", borderRadius: "0.5rem" }}>
        <CardContent sx={{ p: "1rem", height: "100%" }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: "0.875rem", mb: 2 }}
          >
            Revenue vs Target
          </Typography>
          <NoDataFound
            title="No Revenue Data"
            description="Start adding invoices and payments to see revenue trends over time."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%", borderRadius: "0.5rem" }}>
      <CardContent sx={{ p: "1rem" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb="1rem"
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: "0.875rem" }}
          >
            Revenue vs Target
          </Typography>
          <Chip
            label="Last 6 Months"
            variant="outlined"
            size="small"
            sx={{
              fontSize: "0.625rem",
              height: "1.5rem",
            }}
          />
        </Box>
        <Box sx={{ width: "100%", height: "16rem" }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient
                  id="revenueGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#1976d2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1976d2" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                stroke="#6b7280"
                style={{ fontSize: "0.625rem" }}
                tick={{ fill: "#6b7280" }}
              />
              <YAxis
                stroke="#6b7280"
                tickFormatter={(value) => `$${value / 1000}K`}
                style={{ fontSize: "0.625rem" }}
                tick={{ fill: "#6b7280" }}
              />
              <Tooltip
                formatter={(value, name) => [
                  `$${value.toLocaleString()}`,
                  name === "revenue" ? "Actual Revenue" : "Target",
                ]}
                labelStyle={{ color: "#374151", fontSize: "0.75rem" }}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  fontSize: "0.75rem",
                }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#1976d2"
                strokeWidth={2}
                fill="url(#revenueGradient)"
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#ff9800"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

const PracticeAreasChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: "100%", borderRadius: "0.5rem" }}>
        <CardContent sx={{ p: "1rem", height: "100%" }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: "0.875rem", mb: 2 }}
          >
            Revenue by Practice Area
          </Typography>
          <NoDataFound
            title="No Practice Area Data"
            description="Categorize your cases by practice areas to see revenue distribution."
          />
        </CardContent>
      </Card>
    );
  }

  const generateShades = (baseColor, count) => {
    const shades = [];
    for (let i = 0; i < count; i++) {
      const opacity = 0.4 + (0.6 * i) / Math.max(count - 1, 1);
      shades.push(
        `${baseColor}${Math.round(opacity * 255)
          .toString(16)
          .padStart(2, "0")}`
      );
    }
    return shades;
  };

  const baseColors = [
    "#1976d2",
    "#9c27b0",
    "#ff9800",
    "#4caf50",
    "#e91e63",
    "#009688",
    "#3f51b5",
    "#f44336",
  ];

  // Transform data for stacked bar chart
  const chartData = data.map((area, areaIndex) => {
    const matterEntries = Object.entries(area.matters || {});
    const row = { name: area.name };

    matterEntries.forEach(([matterName, amount]) => {
      row[matterName] = amount;
    });

    return row;
  });

  const allMatterNames = Array.from(
    new Set(data.flatMap((area) => Object.keys(area.matters || {})))
  );

  const matterColors = {};
  data.forEach((area, areaIndex) => {
    const baseColor = baseColors[areaIndex % baseColors.length];
    const matters = Object.keys(area.matters || {});
    const shades = generateShades(baseColor, matters.length);

    matters.forEach((matter, matterIndex) => {
      matterColors[matter] = shades[matterIndex] || baseColor;
    });
  });

  return (
    <Card sx={{ height: "100%", borderRadius: "0.5rem" }}>
      <CardContent sx={{ p: "1rem" }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight="bold"
          sx={{ fontSize: "0.875rem" }}
        >
          Revenue by Practice Area
        </Typography>
        <Box sx={{ width: "100%", height: "20rem" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                stroke="#6b7280"
                style={{ fontSize: "0.625rem" }}
                tick={{ fill: "#6b7280" }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis
                tickFormatter={(value) => `$${value / 1000}K`}
                stroke="#6b7280"
                style={{ fontSize: "0.625rem" }}
              />
              <Tooltip
                formatter={(value, name) => [
                  `$${value.toLocaleString()}`,
                  name,
                ]}
                labelFormatter={(label) => `Practice Area: ${label}`}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  fontSize: "0.75rem",
                }}
              />
              {/* <Legend
                wrapperStyle={{ fontSize: "0.625rem" }}
                formatter={(value) =>
                  value.length > 20 ? `${value.substring(0, 20)}...` : value
                }
              /> */}
              {allMatterNames.map((matter) => (
                <Bar
                  key={matter}
                  dataKey={matter}
                  stackId="a"
                  fill={matterColors[matter]}
                  radius={[0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// Matter Status Chart Component
const MatterStatusChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: "100%", borderRadius: "0.5rem" }}>
        <CardContent sx={{ p: "1rem", height: "100%" }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: "0.875rem", mb: 2 }}
          >
            Matter Status Overview
          </Typography>
          <NoDataFound
            title="No Case Data"
            description="Add cases to your system to see matter status distribution."
          />
        </CardContent>
      </Card>
    );
  }

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const statusColors = {
    open: "#4CAF50",
    pending: "#FFC107",
    closed: "#F44336",
  };
  const colors = data.map((item) => statusColors[item.name] || "#1976d2");

  return (
    <Card sx={{ height: "100%", borderRadius: "0.5rem" }}>
      <CardContent sx={{ p: "1rem" }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight="bold"
          sx={{ fontSize: "0.875rem" }}
        >
          Matter Status Overview
        </Typography>
        <Box sx={{ width: "100%", height: "12rem" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [value, "Matters"]}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  fontSize: "0.75rem",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          flexWrap="wrap"
          gap="0.5rem"
          mt="0.5rem"
        >
          {data.map((item, index) => (
            <Box
              key={item.name}
              display="flex"
              alignItems="center"
              gap="0.25rem"
            >
              <Box
                sx={{
                  width: "0.5rem",
                  height: "0.5rem",
                  borderRadius: "50%",
                  bgcolor: colors[index],
                }}
              />
              <Typography
                sx={{
                  color: "text.secondary",
                  fontWeight: "medium",
                  fontSize: "0.625rem",
                }}
              >
                {item.name}: {item.value} (
                {((item.value / total) * 100).toFixed(1)}%)
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

// Recent Activity Component
const RecentActivity = ({ activities }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case "payment":
        return <AttachMoneyIcon />;
      case "matter":
        return <GavelIcon />;
      case "invoice":
        return <ReceiptIcon />;
      case "time":
        return <AccessTimeIcon />;
      case "client":
        return <PeopleIcon />;
      default:
        return <WorkIcon />;
    }
  };

  if (!activities || activities.length === 0) {
    return (
      <Card sx={{ borderRadius: "0.5rem" }}>
        <CardContent sx={{ p: "1rem", height: "100%" }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: "0.875rem", mb: 2 }}
          >
            Recent Activity
          </Typography>
          <NoDataFound
            title="No Recent Activity"
            description="Your recent activities will appear here as you work on cases, add time entries, and process payments."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: "0.5rem" }}>
      <CardContent sx={{ p: "1rem" }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight="bold"
          sx={{ fontSize: "0.875rem" }}
        >
          Recent Activity
        </Typography>
        <List sx={{ p: 0 }}>
          {activities.map((activity, index) => (
            <ListItem
              key={index}
              sx={{
                "&:hover": { bgcolor: "grey.50" },
                borderRadius: "0.375rem",
                px: "0.5rem",
                py: "0.375rem",
                mb: "0.25rem",
              }}
            >
              <ListItemIcon>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: "1.75rem",
                    height: "1.75rem",
                  }}
                >
                  {React.cloneElement(getActivityIcon(activity.type), {
                    sx: { fontSize: "0.875rem" },
                  })}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={activity.activity}
                secondary={new Date(activity.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                primaryTypographyProps={{
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.625rem",
                  color: "text.secondary",
                }}
              />
              {activity.amount && (
                <Chip
                  label={`$${activity.amount.toLocaleString()}`}
                  size="small"
                  sx={{
                    bgcolor: "success.light",
                    color: "success.dark",
                    fontWeight: "bold",
                    fontSize: "0.625rem",
                    height: "1.25rem",
                  }}
                />
              )}
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Top Clients Component
const TopUser = ({ users }) => {
  if (!users || users.length === 0) {
    return (
      <Card sx={{ borderRadius: "0.5rem" }}>
        <CardContent sx={{ p: "1rem", height: "100%" }}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ fontSize: "0.875rem", mb: 2 }}
          >
            Earnings Leaderboard
          </Typography>
          <NoDataFound
            title="No Client Data"
            description="Add clients and track their revenue to see your top performers here."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: "0.5rem" }}>
      <CardContent sx={{ p: "1rem" }}>
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight="bold"
          sx={{ fontSize: "0.875rem" }}
        >
          Earnings Leaderboard
        </Typography>
        <List sx={{ p: 0 }}>
          {users.map((user, index) => (
            <ListItem
              key={index}
              sx={{
                "&:hover": { bgcolor: "grey.50" },
                borderRadius: "0.375rem",
                px: "0.5rem",
                py: "0.375rem",
                mb: "0.25rem",
              }}
            >
              <ListItemIcon>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: "1.75rem",
                    height: "1.75rem",
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                  }}
                >
                  {user.name.charAt(0)}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={user.name}
                secondary={`${user.hours} billable hours`}
                primaryTypographyProps={{
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.625rem",
                  color: "text.secondary",
                }}
              />
              <Box textAlign="right">
                <Typography
                  fontWeight="bold"
                  color="primary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  ${user.revenue.toLocaleString()}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "0.625rem" }}
                >
                  YTD Revenue
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

const FirmDash = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);

  const { token, teamMemberLawyerId, allUsers } = useSelector(
    (state) => state.user
  );
  const { allBilles } = useSelector((state) => state.billing);
  const { allStatement, AllAccounts } = useSelector((state) => state.account);
  const { allActivities } = useSelector((state) => state.activity);
  const { allCase } = useSelector((state) => state.case);
  const { filteredContacts } = useSelector((state) => state.contact);

  useEffect(() => {
    if (!token) return;

    const fetchAll = async (action, url, extra = {}) => {
      const first = await dispatch(
        action({ url, token, page: 1, pageSize: 1, ...extra })
      ).unwrap();
      const total = first?.count || first?.pagination?.rowCount || 0;
      const pageSize = total > 1 ? total : 1;
      await dispatch(action({ url, token, page: 1, pageSize, ...extra }));
    };

    const loadData = async () => {
      await Promise.all([
        fetchAll(getAllBilles, `${backend_api}firm-case/bill/`),
        fetchAll(getAllStatement, `${backend_api}bank/transactions/`, {
          teamMemberLawyerId,
        }),
        fetchAll(getAllAccounts, `${backend_api}bank/accounts/`, {
          teamMemberLawyerId,
        }),
        fetchAll(getActivities, `${backend_api}firm-case/expense/`, {
          teamMemberLawyerId,
        }),
        fetchAll(getCases, `${backend_api}case/register/`),
        fetchAll(getAllFilterContacts, `${backend_api}contact/retrieve/`),
        fetchAll(getAllUsers, `${backend_api}team/create-employee/`),
      ]);
    };

    loadData();
  }, [dispatch, token, teamMemberLawyerId]);

  useEffect(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const totalInvoiced = Array.isArray(allBilles)
      ? allBilles.reduce((sum, b) => sum + Number(b.total_amount || 0), 0)
      : 0;

    const totalPaidInvoices = Array.isArray(allBilles)
      ? allBilles.reduce(
          (sum, b) =>
            b.status === "paid" ? sum + Number(b.total_amount || 0) : sum,
          0
        )
      : 0;

    const lawFirmBalance = Array.isArray(AllAccounts)
      ? AllAccounts.reduce(
          (sum, acc) =>
            acc.account_type === "law firm"
              ? sum + Number(acc.balance || 0)
              : sum,
          0
        )
      : 0;

    const outstandingInvoices = Array.isArray(allBilles)
      ? allBilles.reduce(
          (sum, b) =>
            b.status !== "paid" ? sum + Number(b.total_amount || 0) : sum,
          0
        )
      : 0;

    const totalRevenue =
      totalPaidInvoices > 0 ? totalPaidInvoices : lawFirmBalance;

    const billableHours = Array.isArray(allActivities)
      ? allActivities.reduce((sum, a) => {
          if (
            a.type === "time" &&
            a.time_in &&
            a.time_out &&
            !a.non_billable &&
            new Date(a.time_in).getMonth() === currentMonth &&
            new Date(a.time_in).getFullYear() === currentYear
          ) {
            const diff = new Date(a.time_out) - new Date(a.time_in);
            return sum + diff / (1000 * 60 * 60);
          }
          return sum;
        }, 0)
      : 0;

    const hoursToday = Array.isArray(allActivities)
      ? allActivities.reduce((sum, a) => {
          if (
            a.type === "time" &&
            a.time_in &&
            a.time_out &&
            !a.non_billable &&
            new Date(a.time_in).toDateString() === currentDate.toDateString()
          ) {
            const diff = new Date(a.time_out) - new Date(a.time_in);
            return sum + diff / (1000 * 60 * 60);
          }
          return sum;
        }, 0)
      : 0;

    const billedActivityAmount = Array.isArray(allActivities)
      ? allActivities.reduce((sum, a) => {
          if (!a.non_billable) {
            return sum + Number(a.total_payment || 0);
          }
          return sum;
        }, 0)
      : 0;

    const activeClientsSet = new Set();
    if (Array.isArray(allCase)) {
      allCase.forEach((caseItem) => {
        const status = caseItem.case_status || caseItem.status;
        if (status === "open") {
          const id =
            caseItem.case_related_person?.id ||
            caseItem.client_id ||
            caseItem.contact_id;
          if (id) activeClientsSet.add(id);
        }
      });
    }
    const activeClients = activeClientsSet.size;

    const totalMatters = Array.isArray(allCase)
      ? allCase.filter((c) =>
          ["open", "closed", "close", "pending"].includes(
            c.case_status || c.status
          )
        ).length
      : 0;

    const collectionRate = totalInvoiced
      ? (totalPaidInvoices / totalInvoiced) * 100
      : 0;

    const averageHourlyRate = billableHours
      ? billedActivityAmount / billableHours
      : 0;

    const matterStatusCounts = { open: 0, pending: 0, closed: 0 };
    if (Array.isArray(allCase)) {
      allCase.forEach((caseItem) => {
        const status = caseItem.case_status || caseItem.status || "open";
        if (status === "open") matterStatusCounts.open += 1;
        else if (status === "pending") matterStatusCounts.pending += 1;
        else if (status === "closed" || status === "close")
          matterStatusCounts.closed += 1;
      });
    }
    const matterStatus = Object.keys(matterStatusCounts).map((key) => ({
      name: key,
      value: matterStatusCounts[key],
    }));

    const revenueByMonth = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const monthName = date.toLocaleDateString("en-US", { month: "short" });

      const monthlyBills = Array.isArray(allBilles)
        ? allBilles.filter((bill) => {
            if (bill.status !== "paid") return false;
            const billDate = new Date(bill.created_at || bill.date);
            return (
              billDate.getMonth() === date.getMonth() &&
              billDate.getFullYear() === date.getFullYear()
            );
          })
        : [];

      const monthlyRevenue = monthlyBills.reduce(
        (sum, bill) => sum + Number(bill.total_amount || 0),
        0
      );

      revenueByMonth.push({
        month: monthName,
        revenue: monthlyRevenue,
        target: 250000,
      });
    }

    // Generate practice areas using paid bills linked to matters
    // Generate practice areas using paid bills linked to matters - Updated
    const practiceAreaMap = {};

    if (Array.isArray(allBilles)) {
      allBilles.forEach((bill) => {
        if (bill.status !== "paid") return;
        const caseItem = bill.case || bill.matter;
        if (!caseItem) return;

        const area =
          (caseItem.practice_area &&
            (caseItem.practice_area.name || caseItem.practice_area)) ||
          caseItem.category ||
          "General Practice";

        const matterName =
          caseItem.case_name ||
          caseItem.title ||
          caseItem.name ||
          `Matter #${caseItem.id || "Unknown"}`;

        const revenue = Number(bill.total_amount || 0);

        if (!practiceAreaMap[area]) {
          practiceAreaMap[area] = { name: area, totalRevenue: 0, matters: {} };
        }

        practiceAreaMap[area].totalRevenue += revenue;
        practiceAreaMap[area].matters[matterName] =
          (practiceAreaMap[area].matters[matterName] || 0) + revenue;
      });
    }

    // If no practice areas from bills, generate from cases
    if (Object.keys(practiceAreaMap).length === 0 && Array.isArray(allCase)) {
      allCase.forEach((caseItem) => {
        const area =
          (caseItem.practice_area &&
            (caseItem.practice_area.name || caseItem.practice_area)) ||
          caseItem.category ||
          "General Practice";

        const matterName =
          caseItem.case_name ||
          caseItem.title ||
          caseItem.name ||
          `Matter #${caseItem.id || "Unknown"}`;

        if (!practiceAreaMap[area]) {
          practiceAreaMap[area] = { name: area, totalRevenue: 0, matters: {} };
        }

        // Add placeholder revenue if no bills
        practiceAreaMap[area].matters[matterName] = 1000; // Placeholder amount
        practiceAreaMap[area].totalRevenue += 1000;
      });
    }

    const practiceAreas = Object.values(practiceAreaMap);

    console.log(practiceAreas);
    // Generate recent activity from various sources
    const recentActivity = [];

    // Add bill activities
    if (Array.isArray(allBilles)) {
      allBilles.slice(-3).forEach((bill) => {
        recentActivity.push({
          date: bill.created_at || bill.date || new Date().toISOString(),
          activity: `Invoice #${bill.id || bill.invoice_number} created for ${
            bill.client_name || "Client"
          }`,
          type: "invoice",
          amount: Number(bill.total_amount || 0),
        });
      });
    }

    // Add payment activities
    if (Array.isArray(allStatement)) {
      allStatement
        .filter((t) => t.transaction_type === "deposit")
        .slice(-2)
        .forEach((transaction) => {
          recentActivity.push({
            date: transaction.date || new Date().toISOString(),
            activity: `Payment received - ${
              transaction.description || "Payment"
            }`,
            type: "payment",
            amount: Number(transaction.amount || 0),
          });
        });
    }

    // Add case activities
    if (Array.isArray(allCase)) {
      allCase.slice(-2).forEach((caseItem) => {
        recentActivity.push({
          date:
            caseItem.created_at || caseItem.date || new Date().toISOString(),
          activity: `New matter opened: ${
            caseItem.title || caseItem.name || "Case"
          }`,
          type: "matter",
        });
      });
    }

    // Sort by date and take latest 5
    recentActivity.sort((a, b) => new Date(b.date) - new Date(a.date));
    const sortedRecentActivity = recentActivity.slice(0, 5);

    // Generate top clients from contacts based on revenue
    const topUserData =
      Array.isArray(allUsers) && allUsers.length > 0
        ? allUsers
            ?.map((user) => {
              // Calculate revenue for this client from bills
              const clientBills = Array.isArray(allBilles)
                ? allBilles.filter(
                    (bill) =>
                      (bill.user_id === user.id ||
                        bill.user_name === user.name ||
                        bill.user_name ===
                          `${user.first_name} ${user.last_name}`) &&
                      bill.status === "paid"
                  )
                : [];

              const userRevenue = clientBills.reduce(
                (sum, bill) => sum + Number(bill.total_amount || 0),
                0
              );
              const userHours = clientBills.reduce(
                (sum, bill) => sum + Number(bill.hours || 0),
                0
              );

              return {
                name:
                  user.name ||
                  `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                revenue: userRevenue,
                hours: userHours,
              };
            })
            .filter((user) => user.revenue > 0)
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 5)
        : [];

    const dynamicData = {
      overview: {
        totalRevenue,
        outstandingInvoices,
        billableHours: parseFloat(billableHours.toFixed(2)),
        activeClients,
        totalMatters,
        hoursWorkedToday: parseFloat(hoursToday.toFixed(2)),
        revenueLastMonth:
          revenueByMonth.length > 1
            ? revenueByMonth[revenueByMonth.length - 2].revenue
            : 0,
        billableHoursLastMonth: billableHours,
        activeClientsLastMonth: 0,
        totalMattersLastMonth: 0,
        collectionRate,
        averageHourlyRate,
      },
      revenueByMonth: revenueByMonth.length > 0 ? revenueByMonth : [],
      practiceAreas: practiceAreas.length > 0 ? practiceAreas : [],
      matterStatus: matterStatus.length > 0 ? matterStatus : [],
      recentActivity:
        sortedRecentActivity.length > 0 ? sortedRecentActivity : [],
      topUser: topUserData.length > 0 ? topUserData : [],
    };

    setData(dynamicData);
  }, [
    allBilles,
    allStatement,
    allActivities,
    allCase,
    filteredContacts,
    AllAccounts,
    allUsers,
  ]);

  console.log(data);

  if (!data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Box textAlign="center">
          <CircularProgress
            size={60}
            sx={{ color: "primary.main", mb: "1rem" }}
          />
          <Typography
            variant="h6"
            fontWeight="medium"
            sx={{ fontSize: "1rem" }}
          >
            Loading your firm's dashboard...
          </Typography>
          <Typography
            color="text.secondary"
            mt="0.5rem"
            sx={{ fontSize: "0.75rem" }}
          >
            Gathering the latest data and insights
          </Typography>
        </Box>
      </Box>
    );
  }

  // Calculate percentage changes
  const revenueChange = data.overview.revenueLastMonth
    ? (
        ((data.overview.totalRevenue - data.overview.revenueLastMonth) /
          data.overview.revenueLastMonth) *
        100
      ).toFixed(1)
    : 0;

  const billableHoursChange = data.overview.billableHoursLastMonth
    ? (
        ((data.overview.billableHours - data.overview.billableHoursLastMonth) /
          data.overview.billableHoursLastMonth) *
        100
      ).toFixed(1)
    : 0;

  const clientsChange = data.overview.activeClientsLastMonth
    ? (
        ((data.overview.activeClients - data.overview.activeClientsLastMonth) /
          data.overview.activeClientsLastMonth) *
        100
      ).toFixed(1)
    : 0;

  const mattersChange = data.overview.totalMattersLastMonth
    ? (
        ((data.overview.totalMatters - data.overview.totalMattersLastMonth) /
          data.overview.totalMattersLastMonth) *
        100
      ).toFixed(1)
    : 0;

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          borderRadius: "0 0 1rem 1rem",
          backgroundColor: "primary.main",
        }}
      >
        <Toolbar sx={{ py: "0.5rem" }}>
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              mr: "1rem",
              width: "2rem",
              height: "2rem",
            }}
          >
            <GavelIcon sx={{ color: "white", fontSize: "1.125rem" }} />
          </Avatar>
          <Box flexGrow={1}>
            <Typography
              variant="h5"
              fontWeight="bold"
              color="white"
              sx={{ fontSize: "1.25rem" }}
            >
              Law Firm Analytics
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                mt: "0.125rem",
                fontSize: "0.75rem",
              }}
            >
              Comprehensive insights into your practice performance
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              px: "0.75rem",
              py: "0.375rem",
              bgcolor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "0.5rem",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <CalendarTodayIcon sx={{ color: "white", fontSize: "0.875rem" }} />
            <Box>
              <Typography
                color="white"
                fontWeight="medium"
                sx={{ fontSize: "0.75rem" }}
              >
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "0.625rem",
                }}
              >
                {new Date().getFullYear()}
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: "1rem" }}>
        <Grid container spacing={2} mb={3}>
          {/* Overview Cards */}
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Total Revenue"
              value={`$${data.overview.totalRevenue.toLocaleString()}`}
              change={parseFloat(revenueChange)}
              IconComponent={AttachMoneyIcon}
              subtitle="This month"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Outstanding Invoices"
              value={`$${data.overview.outstandingInvoices.toLocaleString()}`}
              change={-8.3}
              IconComponent={ReceiptIcon}
              subtitle="Pending collection"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Billable Hours"
              value={data.overview.billableHours.toLocaleString()}
              change={parseFloat(billableHoursChange)}
              IconComponent={AccessTimeIcon}
              subtitle="This month"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Active Clients"
              value={data.overview.activeClients}
              change={parseFloat(clientsChange)}
              IconComponent={PeopleIcon}
              subtitle="Currently engaged"
            />
          </Grid>
        </Grid>

        {/* Secondary Metrics */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Total Matters"
              value={data.overview.totalMatters}
              change={parseFloat(mattersChange)}
              IconComponent={WorkIcon}
              subtitle="Active + completed"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Collection Rate"
              value={`${data.overview.collectionRate.toFixed(1)}%`}
              change={2.1}
              IconComponent={AccountBalanceIcon}
              subtitle="Payment efficiency"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Avg Hourly Rate"
              value={`$${data.overview.averageHourlyRate.toFixed(0)}`}
              change={1.8}
              IconComponent={AttachMoneyIcon}
              subtitle="Blended rate"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <OverviewCard
              title="Hours Today"
              value={data.overview.hoursWorkedToday}
              change={0}
              IconComponent={AccessTimeIcon}
              subtitle="Billable time logged"
            />
          </Grid>
        </Grid>

        {/* Charts Row */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} lg={8}>
            <RevenueTrendChart data={data.revenueByMonth} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <MatterStatusChart data={data.matterStatus} />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} lg={8}>
            <PracticeAreasChart data={data.practiceAreas} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <TopUser users={data.topUser} />
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12}>
            <Box
              id="access_firm_dashboard_graph3"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "0.5rem",
                p: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Graph 3
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <RecentActivity activities={data.recentActivity} />
      </Container>
    </Box>
  );
};

export default FirmDash;
